/**
 * MOCK · CANONICAL (CT-5282) — the identification record behind one entity.
 *
 * Derived from the entity-list seed rather than hand-written alongside it, so
 * every record reachable from a list resolves to a header. A seed where the two
 * drift apart produces dead ends between screen 2 and screen 3 that only show
 * up in manual QA.
 *
 * Aliases differ per type for the same reason they do in the list seed: the
 * mapper's job is to flatten them without branching on entity type.
 */
import { getMockEntities } from './entities.mock.js';

const customerHeader = (row) => ({
  entity_id: row.entity_id,
  title: row.title,
  visual: row.visual,
  source: row.source ?? 'Salesforce',
  tags: [
    ...(row.type_tag ? [{ label: row.type_tag }] : [{ label: 'Customer' }]),
    { label: 'Enterprise' },
  ],
  status: row.badges?.[0],
  description: row.description,
  secondary_meta: [
    { label: '12 documents' },
    { label: '4 open' },
    ...(row.meta?.[0] ? [{ label: `${row.meta[0].label}: ${row.meta[0].value}` }] : []),
  ],
});

const employeeHeader = (row) => ({
  entityId: row.entityId,
  name: row.name,
  avatarUrl: row.avatarUrl,
  source: 'Workday',
  tags: [{ label: 'Field operations' }],
  status: row.badges?.[0],
  description: row.summary,
  secondaryMeta: [{ label: 'Team: Service' }],
});

/** Thinly mapped on purpose: title only, so degradation is testable. */
const companyHeader = (row) => ({
  entity_id: row.entity_id,
  title: row.title,
  visual: row.visual,
  source: row.source ?? 'CDK Global',
  tags: [{ label: row.type_tag ?? 'Company' }, { label: 'Key account' }],
  status: row.badges?.[0],
  description: row.description,
  secondary_meta: [
    { label: '8 documents' },
    { label: '2 open' },
    ...(row.meta?.[0] ? [{ label: `${row.meta[0].label}: ${row.meta[0].value}` }] : []),
  ],
});

const vehicleHeader = (row) => ({
  id: row.id,
  label: row.label,
});

const buildSeed = () => {
  const entities = getMockEntities();
  return {
    customers: entities.customers.map(customerHeader),
    employees: entities.employees.map(employeeHeader),
    companies: entities.companies.map(companyHeader),
    vehicles: entities.vehicles.map(vehicleHeader),
    dealerships: [],
  };
};

let mockEntityHeaders = buildSeed();

export const getMockEntityHeaders = () => mockEntityHeaders;

const HEADER_BUILDERS = { customers: customerHeader, companies: companyHeader };

/** Gives a freshly created list row the header its profile will open with. */
export const appendMockEntityHeader = (entityType, row) => {
  const build = HEADER_BUILDERS[entityType];
  if (!build) return;
  mockEntityHeaders = { ...mockEntityHeaders, [entityType]: [build(row), ...mockEntityHeaders[entityType]] };
};

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockEntityHeadersForTests = () => {
  mockEntityHeaders = buildSeed();
};
